import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { APP_IMAGES } from "../../assets";
import globalStyle from "../../globalStyle";
import { baseUrl } from "../../services";
import {
  createEcomCategory,
  updateEcomCategory,
} from "../../services/ecomCategoryApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { addEcomCategoryValidation } from "../../utils/Validation";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import Label from "../Label";
import TextInput from "../TextInputs";
import DialogHeader from "./Header";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "900px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiInputBase-multiline": {
    padding: "6px !important",
  },
}));

const initialInputValues = {
  smallIcon: null,
  sortingNum: 1,
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  metaTitle_en: "",
  metaTitle_ro: "",
  metaDescription_en: "",
  metaDescription_ro: "",
  metaKeywords_en: [],
  metaKeywords_ro: [],
};

let paramsData = {};

const KeywordsInput = ({
  placeholder,
  value = [],
  onChange,
  textFieldProps,
}) => {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]} // no predefined options
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
            key={index}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={true}
          {...textFieldProps}
        />
      )}
    />
  );
};

const AddEditEcomCategoryDialog = forwardRef(({ }, ref) => {
  const { t } = useTranslation(["catalogue", "common"]);
  const openIconFile = useRef(null);
  const openImageFile = useRef(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    translateToEnglish: false,
    save: false,
    image: false,
    smallIcon: false,
  });
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);

  // Redux State
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          image: data?.image || null,
          smallIcon: data?.activeIcon || null,
          sortingNum: data?.serialNo || 0,
          name_en: capitalizeText(data?.name?.en) || "",
          name_ro: capitalizeText(data?.name?.ro) || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          metaTitle_en: capitalizeText(data?.metaTitle?.en) || "",
          metaTitle_ro: capitalizeText(data?.metaTitle?.ro) || "",
          metaDescription_en: data?.metaDescription?.en || "",
          metaDescription_ro: data?.metaDescription?.ro || "",
          metaKeywords_en: data?.metaKeywords?.en || [],
          metaKeywords_ro: data?.metaKeywords?.ro || [],
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleUploadImage = async (event, type) => {
    try {
      setIsLoading((prev) => ({ ...prev, [type]: true }));
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((pre) => ({ ...pre, [type]: false }));
      setInputValues((pre) => ({ ...pre, [type]: response?.data?.url }));
      handleOpenSnackBar("success", t("success.imageUploadSuccess", { ns: "common" }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.imageUploadFailed", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, [type]: false }));
      openImageFile.current.value = null;
      openIconFile.current.value = null;
    }
  };

  const handleAddEditCategory = () => {
    const valid = addEcomCategoryValidation(inputValues, setInputError);

    if (valid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

    let payload = {
      image: inputValues.image,
      activeIcon: inputValues.smallIcon,
      serialNo: inputValues.sortingNum,
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      metaTitle: {
        en: inputValues.metaTitle_en,
        ro: inputValues.metaTitle_ro,
      },
      metaDescription: {
        en: inputValues.metaDescription_en,
        ro: inputValues.metaDescription_ro,
      },
      metaKeywords: {
        en: inputValues.metaKeywords_en,
        ro: inputValues.metaKeywords_ro,
      },
    };

    if (paramsData?.type === "add") {
      handleApiCall(createEcomCategory, payload, "");
    } else {
      handleApiCall(updateEcomCategory, payload, paramsData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData(searchKeys.categoryName);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;

    const formattedValue = type === "number" ? valueAsNumber : value;

    setInputValues((pre) => ({ ...pre, [name]: formattedValue }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  const handleChangeMetaKeywords = (name, value) => {
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };


  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >

        <DialogHeader
          title={paramsData.type === "add" ? t("addNewCategory") : t("editCategory")}
          secondaryHeading={paramsData.type === "add" ? t("addNewCategoryInstruction") : t("editCategoryInstruction")}
          icon={
            <CategoryIcon sx={{}} />
          }
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            {/* Large Image */}

            <Grid item xs={12}>
              <Label labelText={t("image", { ns: "common" })} />
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                border={"1px dashed"}
                borderColor={inputError.image ? "#d32f2f" : "text.secondary"}
                borderRadius={2}
                sx={{
                  cursor: "pointer",
                  height: { xs: 100, md: 120 },
                  padding: 1,
                }}
                onClick={() => openImageFile.current.click()}
              >
                {isLoading.image ? (
                  <CircularProgress size={35} color="inherit" />
                ) : (
                  <img
                    src={APP_IMAGES.uploadImage}
                    alt={"upload-image"}
                    width={100}
                  />
                )}

                <Typography variant="body1" color="text.secondary" mt={1}>
                  {t("uploadImage", { ns: "common" })}
                </Typography>
              </Stack>
              <Typography variant="body2" mt={1} fontStyle={"italic"}>
                {t("largeImageSizeNote", { ns: "common" })}
              </Typography>

              {inputError.image && (
                <FormHelperText sx={{ color: "#d32f2f" }}>
                  {t("validation.imageRequired", { ns: "common" })}
                </FormHelperText>
              )}
              <input
                type="file"
                ref={openImageFile}
                onChange={(e) => handleUploadImage(e, "image")}
                style={{ display: "none" }}
                accept={"image/png, image/jpeg"}
              />
            </Grid>

            {inputValues.image && (
              <Grid item xs={12}>
                <Box
                  borderRadius={2}
                  border={"1px solid"}
                  borderColor={"text.secondary"}
                  p={1}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  height={{ xs: 200, md: 220 }}
                  width={{ xs: 200, md: 220 }}
                  position={"relative"}
                >
                  <Box
                    component={"img"}
                    src={inputValues.image}
                    alt={"upload-image"}
                    width={"95%"}
                    height={"95%"}
                    style={{ objectFit: "contain" }}
                  />

                  <IconButton
                    size="small"
                    onClick={() =>
                      setInputValues((prev) => ({ ...prev, image: null }))
                    }
                    sx={{ position: "absolute", top: 1, right: 1 }}
                  >
                    <CloseIcon fontSize="small" htmlColor="red" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Small Icon */}
            <Grid item xs={12}>
              <Label labelText={t("smallIcon", { ns: "common" })} />
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                border={"1px dashed"}
                borderColor={inputError.smallIcon ? "#d32f2f" : "text.secondary"}
                borderRadius={2}
                sx={{
                  cursor: "pointer",
                  height: { xs: 100, md: 120 },
                  padding: 1,
                }}
                onClick={() => openIconFile.current.click()}
              >
                {isLoading.smallIcon ? (
                  <CircularProgress size={35} color="inherit" />
                ) : (
                  <img
                    src={APP_IMAGES.uploadImage}
                    alt={"upload-icon"}
                    width={100}
                  />
                )}

                <Typography variant="body1" color="text.secondary" mt={1}>
                  {t("uploadIcon", { ns: "common" })}
                </Typography>
              </Stack>
              <Typography variant="body2" mt={1} fontStyle={"italic"}>
                {t("iconDimensionNote", { ns: "common" })}
              </Typography>

              {inputError.smallIcon && (
                <FormHelperText sx={{ color: "#d32f2f" }}>
                  {t("validation.iconRequired", { ns: "common" })}
                </FormHelperText>
              )}
              <input
                type="file"
                ref={openIconFile}
                onChange={(e) => handleUploadImage(e, "smallIcon")}
                style={{ display: "none" }}
                accept={"image/png, image/jpeg"}
              />
            </Grid>

            {inputValues.smallIcon && (
              <Grid item xs={12}>
                <Box
                  borderRadius={2}
                  border={"1px solid"}
                  borderColor={"text.secondary"}
                  p={1}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  height={100}
                  width={100}
                  position={"relative"}
                >
                  <Box
                    component={"img"}
                    src={inputValues.smallIcon}
                    alt={"upload-icon"}
                    width={"95%"}
                    height={"95%"}
                    style={{ objectFit: "contain" }}
                  />

                  <IconButton
                    size="small"
                    onClick={() =>
                      setInputValues((prev) => ({ ...prev, smallIcon: null }))
                    }
                    sx={{ position: "absolute", top: 1, right: 1 }}
                  >
                    <CloseIcon fontSize="small" htmlColor="red" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* <Grid item xs={12} md={6}>
              <Box sx={{ my: { xs: 0, md: 3 } }}>
                <Typography variant="body2">
                  {t("iconDimensionNote")}
                </Typography>
                <Typography
                  variant="body2"
                  display={"inline-block"}
                  color={"primary.main"}
                  sx={{ cursor: "pointer" }}
                  onClick={() => openIconFile.current.click()}
                >
                  {inputValues.smallIcon ? t("change") : t("upload")}
                </Typography>
              </Box>
            </Grid> */}

            <Grid item xs={12}>
              <Label
                required
                labelText={t("order")}
                htmlFor={"ecommerce-category-sorting-number"}
              />
              <TextInput
                styleProps={globalStyle.textField}
                required
                placeholder={t("enterOrder")}
                name={"sortingNum"}
                type={"number"}
                value={inputValues.sortingNum}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.sortingNum)}
                helperText={t(inputError.sortingNum)}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryNameInRomanian")}
                htmlFor={`catrgory-name-ro`}
              />
              <TextInput
                id={`catrgory-name-ro`}
                required
                placeholder={t("enterNameInRomanian")}
                name={`name_ro`}
                textLength={60}
                value={inputValues.name_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_ro)}
                helperText={t(inputError.name_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_ro?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("categoryNameInEnglish")}
                  htmlFor={`catrgory-name-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() => handleTranslateToEnglish("name_ro", "name_en")}
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-name-en`}
                required
                placeholder={t("enterNameInEnglish")}
                name={`name_en`}
                textLength={60}
                value={inputValues.name_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_en)}
                helperText={t(inputError.name_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t(
                  "categoryDescriptionInRomanian",
                )}
                htmlFor={`catrgory-description-ro`}
              />
              <TextInput
                id={`catrgory-description-ro`}
                required
                placeholder={t(
                  "enterDescriptionInRomanian",
                )}
                name={`description_ro`}
                textLength={255}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={t(inputError.description_ro)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryDescriptionInEnglish",
                  )}
                  htmlFor={`catrgory-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-description-en`}
                required
                placeholder={t("enterDescriptionInEnglish")}
                name={`description_en`}
                textLength={255}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={t(inputError.description_en)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_en?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {t("categoryMetaInfo")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryMetaTitleInRomanian")}
                htmlFor={`catrgory-meta-title-ro`}
              />
              <TextInput
                id={`catrgory-meta-title-ro`}
                required
                placeholder={t("enterMetaTitleInRomanian")}
                name={`metaTitle_ro`}
                textLength={255}
                value={inputValues.metaTitle_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_ro)}
                helperText={t(inputError.metaTitle_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaTitleInEnglish",
                  )}
                  htmlFor={`catrgory-meta-title-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("metaTitle_ro", "metaTitle_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-title-en`}
                required
                placeholder={t("enterMetaTitleInEnglish")}
                name={`metaTitle_en`}
                textLength={255}
                value={inputValues.metaTitle_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_en)}
                helperText={t(inputError.metaTitle_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t(
                  "categoryMetaDescriptionInRomanian",
                )}
                htmlFor={`catrgory-meta-description-ro`}
              />
              <TextInput
                id={`catrgory-meta-description-ro`}
                required
                placeholder={t(
                  "enterMetaDescriptionInRomanian",
                )}
                name={`metaDescription_ro`}
                textLength={255}
                value={inputValues.metaDescription_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_ro)}
                helperText={t(inputError.metaDescription_ro)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaDescriptionInEnglish",
                  )}
                  htmlFor={`catrgory-meta-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish(
                      "metaDescription_ro",
                      "metaDescription_en",
                    )
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-description-en`}
                required
                placeholder={t(
                  "enterMetaDescriptionInEnglish",
                )}
                name={`metaDescription_en`}
                textLength={255}
                value={inputValues.metaDescription_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_en)}
                helperText={t(inputError.metaDescription_en)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t("metaKeywordsInRomanian")}
                htmlFor={`catrgory-meta-keywords-ro`}
              />
              <KeywordsInput
                placeholder={t(
                  "enterMetaKeywordsInRomanian",
                )}
                value={inputValues.metaKeywords_ro}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_ro", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_ro),
                  helperText: t(inputError.metaKeywords_ro),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("metaKeywordsInEnglish")}
                htmlFor={`catrgory-meta-keywords-en`}
              />
              <KeywordsInput
                placeholder={t(
                  "enterMetaKeywordsInEnglish",
                )}
                value={inputValues.metaKeywords_en}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_en", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_en),
                  helperText: t(inputError.metaKeywords_en),
                }}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEditCategory}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});

export default AddEditEcomCategoryDialog;
